import { skipToken, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import type { InfiniteData, UseInfiniteQueryResult, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  ICatalogCategory,
  ICatalogTemplate,
  ICatalogPost,
  IDailyCatalogPost,
  ICategoryPosition,
  IPostInCategoryPosition,
  ICursorPaginatedResponse,
  ICreatePostFromFilePayload,
  ICreatePostFromFileResponse,
  IGeneratePostUsingMlResponse,
  IGetMlResultQueryOptions,
  IGetMlResultResponse,
  IListDailyPostsParams,
  ILokaliseKey,
} from '~/types/catalog'

import {
  addPostToCategory,
  createCategory,
  deleteCategory,
  deletePostFromCategory,
  enableCategory,
  enablePostInCategory,
  getCategories,
  mostLikedTemplate,
  popularTemplate,
  recentTemplate,
  searchTemplates,
  listCategoryPosts,
  updateCategoriesPositions,
  updateCategory,
  updatePostsInCategoryPositions,
  createPostFromFile,
  generatePostUsingMl,
  getMlResult,
  createDailyPost,
  getTemplateById,
  listDailyPosts,
  deleteDailyPost,
  getLokaliseKeys,
} from '~/api/catalog'

////////// KEYS //////////

export const keys = {
  all: ['catalog'] as const,
  categories: () => [...keys.all, 'categories'] as const,
  categoryPosts: (categoryName?: string) => [...keys.all, 'category-posts', categoryName] as const,
  dailyPosts: ({ endDate, startDate }: { endDate?: string; startDate?: string }) =>
    [...keys.all, 'daily-posts', startDate, endDate] as const,
  lokaliseKeys: () => [...keys.all, 'lokalise-keys'] as const,
  mlResult: (requestId: string) => [...keys.all, 'ml-result', requestId] as const,
  mostLikedTemplate: (originType?: string) => [...keys.all, 'most-liked-template', originType] as const,
  popularTemplate: (originType?: string) => [...keys.all, 'popular-template', originType] as const,
  recentTemplate: (originType?: string) => [...keys.all, 'recent-template', originType] as const,
  searchTemplates: (query?: string) => [...keys.all, 'search-template', query] as const,
  templateById: (templateId?: string) => [...keys.all, 'post-by-id', templateId] as const,
}

////////// QUERIES //////////

export const useCatalogCategoriesListQuery = (): UseQueryResult<ICatalogCategory[]> => {
  return useQuery({
    queryFn: getCategories,
    queryKey: keys.categories(),
  })
}

export const useGetLokalizeKeysQuery = (): UseQueryResult<ILokaliseKey[]> => {
  return useQuery({
    queryFn: getLokaliseKeys,
    queryKey: keys.lokaliseKeys(),
  })
}

export const useSearchTemplates = ({
  query,
}: {
  query?: string
}): UseInfiniteQueryResult<InfiniteData<ICursorPaginatedResponse<ICatalogTemplate>>> =>
  useInfiniteQuery({
    getNextPageParam: (lastPage: ICursorPaginatedResponse<ICatalogTemplate>) => lastPage.next_page_cursor,
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => searchTemplates(query, pageParam),
    queryKey: keys.searchTemplates(query),
  })

export const useRecentTemplate = ({
  originType,
}: {
  originType?: string
}): UseInfiniteQueryResult<InfiniteData<ICursorPaginatedResponse<ICatalogTemplate>>> =>
  useInfiniteQuery({
    getNextPageParam: (lastPage: ICursorPaginatedResponse<ICatalogTemplate>) => lastPage.next_page_cursor,
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => recentTemplate(originType, pageParam),
    queryKey: keys.recentTemplate(originType),
  })

export const usePopularTemplate = ({
  isEnabled,
  originType,
}: {
  isEnabled: boolean
  originType?: string
}): UseQueryResult<ICatalogTemplate[]> =>
  useQuery({
    queryFn: isEnabled ? () => popularTemplate(originType) : skipToken,
    queryKey: keys.popularTemplate(originType),
  })

export const useMostLikedTemplate = ({
  isEnabled,
  originType,
}: {
  isEnabled: boolean
  originType?: string
}): UseQueryResult<ICatalogTemplate[]> =>
  useQuery({
    queryFn: isEnabled ? () => mostLikedTemplate(originType) : skipToken,
    queryKey: keys.mostLikedTemplate(originType),
  })

export const useCategoryPostsListQuery = (categoryName?: string): UseQueryResult<ICatalogPost[]> => {
  return useQuery({
    queryFn: !!categoryName ? () => listCategoryPosts(categoryName) : skipToken,
    queryKey: keys.categoryPosts(categoryName),
  })
}

export const useGetTemplateById = ({
  isEnabled,
  templateId,
}: {
  isEnabled: boolean
  templateId: string
}): UseQueryResult<ICatalogTemplate> =>
  useQuery({
    queryFn: isEnabled ? () => getTemplateById(templateId) : skipToken,
    queryKey: keys.templateById(templateId as string),
  })
export const useDailyPostsQuery = (params: IListDailyPostsParams): UseQueryResult<IDailyCatalogPost[]> => {
  return useQuery({
    queryFn: () => listDailyPosts(params),
    queryKey: keys.dailyPosts({ endDate: params.endDate, startDate: params.startDate }),
  })
}

//////// MUTATIONS //////////

export const useAddPostToCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { category_name?: string; enabled: boolean; post_id: string },
  unknown
> => {
  return useMutation({ mutationFn: addPostToCategory })
}

export const useCreateCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { category_name: string; enabled: boolean; lokalise_key: string },
  unknown
> => {
  return useMutation({ mutationFn: createCategory })
}

export const useCreateDailyPostMutation = (): UseMutationResult<
  ICatalogTemplate,
  unknown,
  { coloring_template_id: string; day: string },
  unknown
> => {
  return useMutation({ mutationFn: createDailyPost })
}

export const useUpdateCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { category_name: string; lokalise_key: string },
  unknown
> => {
  return useMutation({ mutationFn: updateCategory })
}

export const useDeleteCategoryMutation = (): UseMutationResult<void, unknown, { category_name: string }, unknown> => {
  return useMutation({ mutationFn: deleteCategory })
}

export const useUpdateCategoriesPositionsMutation = (): UseMutationResult<
  void,
  unknown,
  { positions: ICategoryPosition[] },
  unknown
> => {
  return useMutation({ mutationFn: updateCategoriesPositions })
}

export const useEnableCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { category_name: string; enabled: boolean },
  unknown
> => {
  return useMutation({ mutationFn: enableCategory })
}

export const useDeletePostFromCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { categoryName: string; postId: string },
  unknown
> => {
  return useMutation({ mutationFn: deletePostFromCategory })
}

export const useEnablePostInCategoryMutation = (): UseMutationResult<
  void,
  unknown,
  { categoryName: string; enabled: boolean; postId: string },
  unknown
> => {
  return useMutation({ mutationFn: enablePostInCategory })
}

export const useUpdatePostsInCategoryPositionsMutation = (): UseMutationResult<
  void,
  unknown,
  { positions: IPostInCategoryPosition[] },
  unknown
> => {
  return useMutation({ mutationFn: updatePostsInCategoryPositions })
}

export const useMlResultQuery = (
  requestId: string,
  options?: IGetMlResultQueryOptions
): UseQueryResult<IGetMlResultResponse> => {
  return useQuery({
    enabled: options?.enabled !== false,
    queryFn: requestId ? () => getMlResult(requestId) : skipToken,
    queryKey: keys.mlResult(requestId),
    refetchInterval: options?.refetchInterval,
  })
}

export const useCreatePostFromFileMutation = (): UseMutationResult<
  ICreatePostFromFileResponse,
  unknown,
  ICreatePostFromFilePayload
> => {
  return useMutation({ mutationFn: createPostFromFile })
}

export const useGeneratePostUsingMlMutation = (): UseMutationResult<
  IGeneratePostUsingMlResponse,
  unknown,
  { prompt: string }
> => {
  return useMutation({ mutationFn: generatePostUsingMl })
}

export const useDeleteDailyPostMutation = (): UseMutationResult<void, unknown, string> => {
  return useMutation({ mutationFn: deleteDailyPost })
}
